import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import axios from 'axios';

// Admin token for API calls - set in .env.local as REACT_APP_ADMIN_TOKEN. No hardcoded
// fallback: a misconfigured build should fail admin auth loudly, not silently grant access
// via a value baked into source control.
const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN;

// Helper function to get admin headers
const getAdminHeaders = () => ({
  'X-Admin-Token': ADMIN_TOKEN
});

/**
 * Fetch all user feedback
 * Backend endpoint: GET /api/admin/feedback/list
 * Expected response: { status: "success", data: { feedback: [{ id, username, email, feedback_type, title, description, created_at }] } }
 */
export const fetchAllFeedback = createAsyncThunk('adminFeedback/fetchAll', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get('/api/admin/feedback/list', {
      headers: getAdminHeaders()
    });

    if (response.data.status === 'success') {
      return response.data.data.feedback;
    }
    return rejectWithValue(response.data.message || 'Failed to fetch feedback');
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message);
  }
});

const initialState = {
  feedback: [],
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  searchTerm: '',
  filterType: 'all'
};

const adminFeedbackSlice = createSlice({
  name: 'adminFeedback',
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setFilterType: (state, action) => {
      state.filterType = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllFeedback.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAllFeedback.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.feedback = action.payload;
      })
      .addCase(fetchAllFeedback.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

export const { setSearchTerm, setFilterType, clearError } = adminFeedbackSlice.actions;

// Selectors
export const selectAllFeedback = (state) => state.adminFeedback.feedback;
export const selectFeedbackTypes = createSelector([selectAllFeedback], (feedback) => [
  ...new Set(feedback.map((entry) => entry.feedback_type))
]);
export const selectFilteredFeedback = createSelector(
  [selectAllFeedback, (state) => state.adminFeedback.searchTerm, (state) => state.adminFeedback.filterType],
  (feedback, searchTerm, filterType) => {
    return feedback.filter((entry) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        searchTerm === '' ||
        (entry.username && entry.username.toLowerCase().includes(term)) ||
        (entry.email && entry.email.toLowerCase().includes(term)) ||
        entry.title.toLowerCase().includes(term) ||
        entry.description.toLowerCase().includes(term);

      const matchesType = filterType === 'all' || entry.feedback_type === filterType;

      return matchesSearch && matchesType;
    });
  }
);

export default adminFeedbackSlice.reducer;
